// src/pages/InvoicesPage.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";
import useBusinessGuard from "../hooks/useBusinessGuard";

export default function InvoicesPage() {
  const { authId, loading } = useBusinessGuard();
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);

  useEffect(() => {
    if (!authId) return;

    const loadInvoices = async () => {
      setLoadingInvoices(true);

      // 1) Resolve business.business_uuid from auth_id
      const { data: business, error: bizErr } = await supabase
        .from("businesses")
        .select("business_uuid")
        .eq("auth_id", authId)
        .single();

      if (bizErr || !business) {
        console.error("[InvoicesPage] business lookup error:", bizErr);
        setInvoices([]);
        setLoadingInvoices(false);
        return;
      }

      // 2) Load invoices for this business_uuid
      const { data, error } = await supabase
        .from("invoices")
        .select("id, amount_paid, paid_at, stripe_session_id, metadata")
        .eq("business_id", business.business_uuid)
        .order("paid_at", { ascending: false });

      if (error) {
        console.error("[InvoicesPage] load error:", error);
        setInvoices([]);
      } else {
        setInvoices(data || []);
      }

      setLoadingInvoices(false);
    };

    loadInvoices();
  }, [authId]);

  if (loading) {
    return (
      <Box p={8}>
        <Text>Loading account…</Text>
      </Box>
    );
  }

  return (
    <Box p={8}>
      <Heading mb={4}>Invoices</Heading>
      <Text mb={6} color="gray.600">
        All payments and invoices associated with your campaigns.
      </Text>

      {loadingInvoices ? (
        <Text>Loading invoices…</Text>
      ) : invoices.length === 0 ? (
        <Text>No invoices yet.</Text>
      ) : (
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Invoice ID</Th>
              <Th isNumeric>Amount paid</Th>
              <Th>Paid at</Th>
              <Th>Stripe session</Th>
            </Tr>
          </Thead>
          <Tbody>
            {invoices.map((inv) => (
              <Tr key={inv.id}>
                <Td>{inv.id}</Td>
                <Td isNumeric>
                  {inv.amount_paid != null
                    ? `£${Number(inv.amount_paid).toFixed(2)}`
                    : "—"}
                </Td>
                <Td>
                  {inv.paid_at
                    ? new Date(inv.paid_at).toLocaleString("en-GB")
                    : "—"}
                </Td>
                <Td>{inv.stripe_session_id || "—"}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Box>
  );
}
